import { useState, useRef, useEffect } from 'react';
import {
  MessageSquare,
  Send,
  Loader2,
  Clock,
  BarChart3,
  Table2,
  Type,
  Sparkles,
  X,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from 'recharts';
import clsx from 'clsx';
import type { NLQueryResponse } from '@/types';
import { nlQueryApi } from '@/api/client';
import { useAppStore } from '@/store';

// --- Types -------------------------------------------------------------------

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  response?: NLQueryResponse;
  timestamp: string;
  isLoading?: boolean;
}

// --- Chart Colors ------------------------------------------------------------

const CHART_COLORS = ['#1e40af', '#3b82f6', '#60a5fa', '#93c5fd', '#2563eb', '#1d4ed8', '#7c3aed', '#8b5cf6'];

// --- Confidence Badge --------------------------------------------------------

function ConfidenceBadge({ confidence }: { confidence: number }) {
  const level = confidence >= 0.8 ? 'high' : confidence >= 0.5 ? 'medium' : 'low';
  const config = {
    high: { label: 'High Confidence', bg: 'bg-green-50', text: 'text-green-700' },
    medium: { label: 'Medium Confidence', bg: 'bg-amber-50', text: 'text-amber-700' },
    low: { label: 'Low Confidence', bg: 'bg-red-50', text: 'text-red-700' },
  };
  const c = config[level];
  return (
    <span className={clsx('badge text-xs', c.bg, c.text)}>
      {c.label} ({Math.round(confidence * 100)}%)
    </span>
  );
}

// --- Visualization Renderer --------------------------------------------------

function RenderVisualization({ response }: { response: NLQueryResponse }) {
  const { visualization_hint, data } = response;

  if (!data) {
    return <p className="text-sm text-gray-600">{response.interpretation}</p>;
  }

  const chartData = Array.isArray(data) ? data : data.rows || data.items || [];

  if (visualization_hint === 'bar_chart' && chartData.length > 0) {
    const keys = Object.keys(chartData[0]).filter((k) => k !== 'name' && k !== 'label' && k !== 'category');
    const labelKey = Object.keys(chartData[0]).find((k) => k === 'name' || k === 'label' || k === 'category') || keys[0];
    const valueKeys = keys.filter((k) => typeof chartData[0][k] === 'number');

    return (
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={chartData} barSize={28}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
          <XAxis dataKey={labelKey} axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
          <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
          <Tooltip
            contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px' }}
          />
          <Legend />
          {valueKeys.map((key, i) => (
            <Bar key={key} dataKey={key} fill={CHART_COLORS[i % CHART_COLORS.length]} radius={[4, 4, 0, 0]} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    );
  }

  if (visualization_hint === 'line_chart' && chartData.length > 0) {
    const keys = Object.keys(chartData[0]);
    const labelKey = keys.find((k) => k === 'period' || k === 'date' || k === 'name') || keys[0];
    const valueKeys = keys.filter((k) => k !== labelKey && typeof chartData[0][k] === 'number');

    return (
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
          <XAxis dataKey={labelKey} axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
          <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
          <Tooltip
            contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px' }}
          />
          <Legend />
          {valueKeys.map((key, i) => (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              stroke={CHART_COLORS[i % CHART_COLORS.length]}
              strokeWidth={2}
              dot={{ fill: CHART_COLORS[i % CHART_COLORS.length], r: 4 }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    );
  }

  if (visualization_hint === 'pie_chart' && chartData.length > 0) {
    const keys = Object.keys(chartData[0]);
    const labelKey = keys.find((k) => typeof chartData[0][k] === 'string') || keys[0];
    const valueKey = keys.find((k) => typeof chartData[0][k] === 'number') || keys[1];

    return (
      <div className="flex items-center justify-center">
        <ResponsiveContainer width={400} height={300}>
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              outerRadius={110}
              innerRadius={60}
              dataKey={valueKey}
              nameKey={labelKey}
              paddingAngle={2}
              label={({ name, percent }: { name: string; percent: number }) => `${name} ${(percent * 100).toFixed(0)}%`}
            >
              {chartData.map((_: any, index: number) => (
                <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px' }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (visualization_hint === 'table' && chartData.length > 0) {
    const columns = Object.keys(chartData[0]);
    return (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              {columns.map((col) => (
                <th key={col} className="table-header capitalize">{col.replace(/_/g, ' ')}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {chartData.map((row: any, i: number) => (
              <tr key={i} className="border-b border-gray-100 hover:bg-gray-50/50">
                {columns.map((col) => (
                  <td key={col} className={clsx('table-cell text-sm', typeof row[col] === 'number' ? 'text-right font-mono' : '')}>
                    {typeof row[col] === 'number'
                      ? new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(row[col])
                      : String(row[col] ?? '')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Default: text response
  return <p className="text-sm text-gray-700 whitespace-pre-wrap">{response.interpretation}</p>;
}

// --- Suggestion Chips --------------------------------------------------------

const defaultSuggestions = [
  'What is total revenue this period?',
  'Show me expenses by entity',
  'Compare assets vs liabilities',
  'Top 5 accounts by balance',
  'Revenue trend over last 6 periods',
  'Which entity has the highest profit margin?',
];

// --- NL Query Page -----------------------------------------------------------

export default function NLQuery() {
  const { addNotification } = useAppStore();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isQuerying, setIsQuerying] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>(defaultSuggestions);
  const [showHistory, setShowHistory] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Load suggestions
  useEffect(() => {
    nlQueryApi.suggestions().then(setSuggestions).catch(() => setSuggestions(defaultSuggestions));
  }, []);

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSubmit = async (query?: string) => {
    const q = query || input.trim();
    if (!q || isQuerying) return;

    const userMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: q,
      timestamp: new Date().toISOString(),
    };

    const loadingMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
      isLoading: true,
    };

    setMessages((prev) => [...prev, userMsg, loadingMsg]);
    setInput('');
    setIsQuerying(true);

    try {
      const response = await nlQueryApi.submit(q);
      setMessages((prev) =>
        prev.map((m) =>
          m.id === loadingMsg.id
            ? { ...m, content: response.interpretation, response, isLoading: false }
            : m,
        ),
      );
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      setMessages((prev) =>
        prev.map((m) =>
          m.id === loadingMsg.id
            ? {
                ...m,
                content: apiErr.detail || 'Failed to process query. Please try again.',
                isLoading: false,
              }
            : m,
        ),
      );
      addNotification({ type: 'error', title: 'Query Failed', message: apiErr.detail || 'Failed to process query.' });
    } finally {
      setIsQuerying(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const userQueries = messages.filter((m) => m.role === 'user');

  return (
    <div className="flex h-[calc(100vh-7rem)] gap-4">
      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-violet-700" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Natural Language Query</h1>
              <p className="text-sm text-gray-500 mt-0.5">Ask questions about your financial data in plain English</p>
            </div>
          </div>
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="btn-secondary text-sm"
          >
            <Clock className="w-4 h-4 mr-1.5" />
            History
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto card p-4 mb-4">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <Sparkles className="w-12 h-12 text-violet-300 mb-3" />
              <h3 className="text-base font-semibold text-gray-900 mb-1">Ask anything about your data</h3>
              <p className="text-sm text-gray-500 mb-6 max-w-md">
                Query your financial data using natural language. Get charts, tables, and insights instantly.
              </p>
              <div className="flex flex-wrap gap-2 justify-center max-w-lg">
                {suggestions.slice(0, 6).map((s) => (
                  <button
                    key={s}
                    onClick={() => handleSubmit(s)}
                    className="px-3 py-1.5 text-sm bg-violet-50 text-violet-700 rounded-full hover:bg-violet-100 transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={clsx(
                    'flex gap-3',
                    msg.role === 'user' ? 'justify-end' : 'justify-start',
                  )}
                >
                  {msg.role === 'assistant' && (
                    <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center mt-1">
                      <Sparkles className="w-4 h-4 text-violet-600" />
                    </div>
                  )}
                  <div
                    className={clsx(
                      'max-w-[80%] rounded-xl px-4 py-3',
                      msg.role === 'user'
                        ? 'bg-primary-700 text-white'
                        : 'bg-gray-50 border border-gray-200',
                    )}
                  >
                    {msg.isLoading ? (
                      <div className="flex items-center gap-2 text-gray-500">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span className="text-sm">Interpreting<span className="animate-pulse">...</span></span>
                      </div>
                    ) : msg.role === 'user' ? (
                      <p className="text-sm">{msg.content}</p>
                    ) : (
                      <div className="space-y-3">
                        {msg.response && (
                          <>
                            <div className="flex items-center gap-2 mb-2">
                              <ConfidenceBadge confidence={msg.response.confidence} />
                              {msg.response.visualization_hint && (
                                <span className="badge bg-gray-100 text-gray-600 text-xs">
                                  {msg.response.visualization_hint === 'bar_chart' && <BarChart3 className="w-3 h-3 mr-1" />}
                                  {msg.response.visualization_hint === 'table' && <Table2 className="w-3 h-3 mr-1" />}
                                  {msg.response.visualization_hint === 'text' && <Type className="w-3 h-3 mr-1" />}
                                  {msg.response.visualization_hint.replace('_', ' ')}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 italic mb-2">{msg.response.interpretation}</p>
                            <RenderVisualization response={msg.response} />
                          </>
                        )}
                        {!msg.response && (
                          <p className="text-sm text-gray-700">{msg.content}</p>
                        )}
                      </div>
                    )}
                    <p className={clsx(
                      'text-[10px] mt-1.5',
                      msg.role === 'user' ? 'text-primary-200' : 'text-gray-400',
                    )}>
                      {new Date(msg.timestamp).toLocaleTimeString()}
                    </p>
                  </div>
                  {msg.role === 'user' && (
                    <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-primary-700 flex items-center justify-center mt-1">
                      <span className="text-xs font-bold text-white">U</span>
                    </div>
                  )}
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Suggestion chips (when there are messages) */}
        {messages.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {suggestions.slice(0, 4).map((s) => (
              <button
                key={s}
                onClick={() => handleSubmit(s)}
                disabled={isQuerying}
                className="px-2.5 py-1 text-xs bg-gray-50 text-gray-600 rounded-full hover:bg-gray-100 transition-colors border border-gray-200"
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {/* Input */}
        <div className="relative">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask a question about your financial data..."
            rows={2}
            className="w-full px-4 py-3 pr-14 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500 resize-none"
          />
          <button
            onClick={() => handleSubmit()}
            disabled={!input.trim() || isQuerying}
            className={clsx(
              'absolute right-3 bottom-3 p-2 rounded-lg transition-colors',
              input.trim() && !isQuerying
                ? 'bg-violet-600 text-white hover:bg-violet-700'
                : 'bg-gray-100 text-gray-400',
            )}
          >
            {isQuerying ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>

      {/* History Panel */}
      {showHistory && (
        <div className="w-72 flex-shrink-0 card p-4 overflow-hidden flex flex-col">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900">Recent Queries</h3>
            <button onClick={() => setShowHistory(false)} className="p-1 hover:bg-gray-100 rounded">
              <X className="w-3.5 h-3.5 text-gray-400" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto space-y-2">
            {userQueries.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-8">No queries yet</p>
            ) : (
              userQueries.map((q) => (
                <button
                  key={q.id}
                  onClick={() => handleSubmit(q.content)}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors group"
                >
                  <p className="text-xs text-gray-700 line-clamp-2 group-hover:text-primary-700">{q.content}</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">
                    {new Date(q.timestamp).toLocaleTimeString()}
                  </p>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
